import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { useEventDispatcher } from '../../../eventing/components/eventing-service-provider/eventing-service-provider';
import { EventName } from '../../../eventing/interfaces/events.interface';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { Dropdown } from '../../../shared/components/dropdown/dropdown';
import { Field } from '../../../shared/components/field/field';
import { Input } from '../../../shared/components/input/input';
import { TitledSection } from '../../../shared/components/titled-section/titled-section';
import { useToast } from '../../../shared/hooks/use-toast';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import { userCountryList } from '../../../shared/utils/countries';
import { formatDate } from '../../../shared/utils/date-format';
import { userPhonePrefixList } from '../../../shared/utils/phone-prefix';
import { slugify } from '../../../shared/utils/strings';
import { usePasswordReset } from '../../hooks/use-password-reset';
import {
    useGetUserProfileQuery,
    useUpdateUserProfileMutation,
} from '../../queries/profile.graphql';

import type { IUserProfile } from './profile.interface';
import styles from './profile.module.scss';

const style = createStyleHelper(styles, 'profile');

export interface IProfileProps {
    className?: string;
}

export const UserProfile: FunctionComponent<IProfileProps> = ({ className }) => {
    const intl = useIntl();
    const auth = useAuth();
    const toast = useToast();
    const userProfileId = auth?.user?.profile.user_profile_id as string;
    const resetLink = usePasswordReset();
    const {
        register,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<IUserProfile>();

    const [{ fetching: isSubmitting }, execute] = useUpdateUserProfileMutation();

    const [{ data: userProfiledata }, reload] = useGetUserProfileQuery({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId,
        },
        requestPolicy: 'cache-and-network',
    });

    const dispatchEvent = useEventDispatcher();

    const onSubmit = useCallback(
        async (userProfileUpdateData: IUserProfile) => {
            const { error } = await execute({
                xTenantID: config.TENANT_ID,
                userProfileId: userProfileId,
                userProfile: {
                    user: {
                        email: userProfileUpdateData.email,
                        givenName: userProfileUpdateData.name,
                        familyName: userProfileUpdateData.surname,
                        birthDate: userProfileUpdateData.dob,
                        // telephonePrefix: userProfileUpdateData.mobilePrefix,
                        telephone: userProfileUpdateData.mobileNumber.toString(),
                        address: {
                            streetAddress: userProfileUpdateData.address,
                            addressLocality: userProfileUpdateData.city,
                            addressCountry: userProfileUpdateData.country,
                            postalCode: userProfileUpdateData.postcode,
                        },
                    },
                },
            });

            if (error) {
                toast.error(getErrorMessage(intl, 'user.profile.update', error));
                return;
            }

            toast.success(
                intl.formatMessage({
                    id: 'user.profile.update.success',
                    defaultMessage: 'User Profile Updated Successfully',
                }),
            );
            dispatchEvent({ name: EventName.USER_PROFILE_UPDATE });

            reload();
        },
        [execute, dispatchEvent, userProfileId, toast, intl, reload],
    );

    function errorMessage(errorType: keyof IUserProfile): string {
        const message = errors?.[errorType]?.message;
        const type = errors?.[errorType]?.type;
        if (message) {
            return message;
        } else if (type) {
            return intl.formatMessage({
                id: `account.profile.error.${slugify(type)}`,
            });
        }
        return '';
    }

    const userProfile = userProfiledata?.userProfile.user;

    useEffect(() => {
        reset({
            email: userProfile?.email ?? '',
            name: userProfile?.givenName ?? '',
            surname: userProfile?.familyName ?? '',
            dob: formatDate(
                new Date(userProfile?.birthDate ? userProfile?.birthDate : new Date()),
                '-',
            ),
            mobilePrefix: '000', // TODO: https://github.com/GiGInnovationLabs/dragon/issues/242
            mobileNumber: userProfile?.telephone ?? '',
            address: userProfile?.address?.streetAddress ?? '',
            city: userProfile?.address?.addressLocality ?? '',
            country: userProfile?.address?.addressCountry ?? '',
            postcode: userProfile?.address?.postalCode ?? '',
        });
    }, [reset, userProfile]);

    return (
        <form className={createClassName([className, style()])} onSubmit={handleSubmit(onSubmit)}>
            <TitledSection
                title={intl.formatMessage({
                    id: 'account.profile.account-information',
                    defaultMessage: 'Account Information',
                })}
            >
                <div className={style('account-info')}>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.email',
                            defaultMessage: 'Email',
                        })}
                        className={style('email')}
                        dark
                        message={errorMessage('email')}
                        hasError={errors?.email ? true : false}
                    >
                        <Input
                            {...register('email', {
                                required: true,
                                minLength: 2,
                                maxLength: 32,
                            })}
                            type="text"
                            placeholder={intl.formatMessage({
                                id: 'account.profile.email',
                                defaultMessage: 'Email',
                            })}
                            className={style('email-input')}
                            name="email"
                            disabled={true}
                        />
                    </Field>
                    <div className={style('password-change')}>
                        <FormattedMessage
                            id="account.profile.change-password"
                            defaultMessage="To change your password please <reset-link>click here</reset-link>"
                            values={{
                                ['reset-link']: (text) => (
                                    <a className={style('action')} href={resetLink}>
                                        {text}
                                    </a>
                                ),
                            }}
                        />
                    </div>
                </div>
            </TitledSection>
            <TitledSection
                title={intl.formatMessage({
                    id: 'account.profile.personal-information',
                    defaultMessage: 'Personal Information',
                })}
            >
                <div className={style('personal-information')}>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.name',
                            defaultMessage: 'Name',
                        })}
                        className={style('name')}
                        dark
                        message={errorMessage('name')}
                        hasError={errors?.name ? true : false}
                    >
                        <Input
                            {...register('name', {
                                required: true,
                                minLength: 2,
                                maxLength: 32,
                            })}
                            type="text"
                            placeholder={intl.formatMessage({
                                id: 'account.profile.name',
                                defaultMessage: 'Name',
                            })}
                            className={style('name-input')}
                            name="name"
                        />
                    </Field>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.surname',
                            defaultMessage: 'Surname',
                        })}
                        className={style('surname')}
                        dark
                        message={errorMessage('surname')}
                        hasError={errors?.surname ? true : false}
                    >
                        <Input
                            {...register('surname', {
                                required: true,
                                minLength: 2,
                                maxLength: 32,
                            })}
                            placeholder={intl.formatMessage({
                                id: 'account.profile.surname',
                                defaultMessage: 'Surname',
                            })}
                            className={style('surname-input')}
                            name="surname"
                        />
                    </Field>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.date-of-birth',
                            defaultMessage: 'Date Of Birth',
                        })}
                        className={style('dob')}
                        dark
                        message={errorMessage('dob')}
                        hasError={errors?.dob ? true : false}
                    >
                        <Input
                            {...register('dob', { required: true })}
                            type="date"
                            placeholder={intl.formatMessage({
                                id: 'account.profile.date-of-birth',
                                defaultMessage: 'Date Of Birth',
                            })}
                            className={style('dob-input')}
                            name="dob"
                        />
                    </Field>
                    <div className={style('mobile')}>
                        <Field
                            label={intl.formatMessage({
                                id: 'account.profile.mobile-prefix',
                                defaultMessage: 'Mobile Prefix',
                            })}
                            className={style('mobile-prefix')}
                            dark
                            message={errorMessage('mobilePrefix')}
                            hasError={errors?.mobilePrefix ? true : false}
                        >
                            <Dropdown
                                className={style('mobilePrefix-input')}
                                {...register('mobilePrefix', { required: true })}
                                items={userPhonePrefixList}
                            />
                        </Field>
                        <Field
                            label={intl.formatMessage({
                                id: 'account.profile.mobile-number',
                                defaultMessage: 'Mobile Number',
                            })}
                            className={style('mobile-number')}
                            dark
                            message={errorMessage('mobileNumber')}
                            hasError={errors?.mobileNumber ? true : false}
                        >
                            <Input
                                {...register('mobileNumber', {
                                    required: true,
                                    minLength: 5,
                                })}
                                type="number"
                                placeholder={intl.formatMessage({
                                    id: 'account.profile.mobile-number',
                                    defaultMessage: 'Mobile Number',
                                })}
                                className={style('mobileNumber-input')}
                                name="mobileNumber"
                            />
                        </Field>
                    </div>

                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.street-address',
                            defaultMessage: 'Street Address',
                        })}
                        className={style('address')}
                        dark
                        message={errorMessage('address')}
                        hasError={errors?.address ? true : false}
                    >
                        <Input
                            {...register('address', {
                                required: true,
                                minLength: 10,
                                maxLength: 80,
                            })}
                            type="text"
                            placeholder={intl.formatMessage({
                                id: 'account.profile.street-address',
                                defaultMessage: 'Street Address',
                            })}
                            className={style('streetAddress-input')}
                            name="address"
                        />
                    </Field>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.city',
                            defaultMessage: 'City',
                        })}
                        className={style('city')}
                        dark
                        message={errorMessage('city')}
                        hasError={errors?.city ? true : false}
                    >
                        <Input
                            {...register('city', {
                                required: true,
                                minLength: 2,
                                maxLength: 40,
                            })}
                            type="text"
                            placeholder={intl.formatMessage({
                                id: 'account.profile.city',
                                defaultMessage: 'City',
                            })}
                            className={style('city-input')}
                            name="city"
                        />
                    </Field>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.country',
                            defaultMessage: 'Country',
                        })}
                        className={style('country')}
                        dark
                        message={errorMessage('country')}
                        hasError={errors?.country ? true : false}
                    >
                        <Dropdown
                            className={style('country-input')}
                            {...register('country', {
                                required: true,
                            })}
                            items={userCountryList}
                        />
                    </Field>
                    <Field
                        label={intl.formatMessage({
                            id: 'account.profile.postcode',
                            defaultMessage: 'Postcode',
                        })}
                        className={style('postcode')}
                        dark
                        message={errorMessage('postcode')}
                        hasError={errors?.postcode ? true : false}
                    >
                        <Input
                            {...register('postcode', {
                                required: true,
                                minLength: 2,
                                maxLength: 30,
                            })}
                            type="text"
                            placeholder={intl.formatMessage({
                                id: 'account.profile.postcode',
                                defaultMessage: 'Postcode',
                            })}
                            className={style('postcode-input')}
                            name="postcode"
                        />
                    </Field>
                    <div className={style('button')}>
                        <Button
                            className={style('button-save')}
                            size={ButtonSize.MEDIUM}
                            disabled={isSubmitting}
                            type="submit"
                        >
                            <FormattedMessage
                                id="account.profile.save-button"
                                defaultMessage="Save"
                            />
                        </Button>
                    </div>
                </div>
            </TitledSection>
        </form>
    );
};
